import { z } from "zod";
import { IsoDateTime, PRODUCTS } from "../common.ts";

// The Fleet API of the control plane console (`/explorer/v1/platform/fleet/*`, change
// add-control-plane-console, design D1-D9; spec control-plane-console): served by the module `fleet`
// in the `explorer` role of the platform installation only, behind the release flag `fleet.console`.
// Every route needs a live console session and `platform.fleet.manage` (anyone else gets `404`).
// Every change of a customer installation carries the step-up (`step_up`: the Platform
// Administrator's password and current TOTP code), confirmed by `iam` in the same call; the halt
// does not. Nothing here carries player data, operator data of an installation or a secret.

/** Release flag of the Fleet tab and its API (design D1): on only in the platform installation. */
export const FLEET_CONSOLE_FLAG = "fleet.console";

/** Customer slug (3-12 lowercase letters or digits, from a letter; reserved names are refused by the registry's rules). */
export const FleetCustomerV1 = z
  .string()
  .regex(/^[a-z][a-z0-9]{2,11}$/)
  .meta({ id: "FleetCustomerV1" });
export const FleetEnvironmentV1 = z
  .enum(["staging", "production"])
  .meta({ id: "FleetEnvironmentV1" });
export type FleetEnvironmentV1 = z.infer<typeof FleetEnvironmentV1>;
/** A full commit SHA of `main` (a build). */
export const FleetCommitShaV1 = z
  .string()
  .regex(/^[0-9a-f]{40}$/)
  .meta({ id: "FleetCommitShaV1" });
/** A published version (ADR 0012). */
export const FleetVersionV1 = z
  .string()
  .regex(/^v(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/)
  .meta({ id: "FleetVersionV1" });

/** The installation states of the registry (adopt-installation-per-customer D4). */
export const FleetInstallationStateV1 = z
  .enum([
    "provisioning",
    "active",
    "rolling-out",
    "halted",
    "failed-forward",
    "parked",
    "failed",
    "destroying",
    "destroyed",
  ])
  .meta({ id: "FleetInstallationStateV1" });
export type FleetInstallationStateV1 = z.infer<typeof FleetInstallationStateV1>;

export const FleetHealthStatusV1 = z
  .enum(["healthy", "warning", "critical", "in_progress", "unknown"])
  .meta({ id: "FleetHealthStatusV1" });
export type FleetHealthStatusV1 = z.infer<typeof FleetHealthStatusV1>;

/** The health of an installation and the source that decided it (spec "Installation health"). */
export const FleetHealthV1 = z
  .object({
    status: FleetHealthStatusV1,
    source: z.enum(["registry", "drift", "metrics", "none"]),
    reason: z.string().max(500).nullable(),
    /** The reason as a stable code, translated by the console; `reason` stays the English detail. */
    code: z.enum([
      "healthy",
      "destroyed",
      "state_critical",
      "state_in_progress",
      "no_drift_check",
      "drift_found",
      "drift_stale",
      "metrics_unreadable",
      "alarm",
    ]),
    /** The identifiers the code names: the state, the drift run, the alarm names. */
    data: z.string().max(500).nullable(),
    /** The English detail the translated message ends with (the problems a drift check found). */
    detail: z.string().max(1000).nullable(),
  })
  .strict()
  .meta({ id: "FleetHealthV1" });
export type FleetHealthV1 = z.infer<typeof FleetHealthV1>;

/** The last drift check of an installation (`control-plane-drift.yml`). */
export const FleetDriftV1 = z
  .object({
    ok: z.boolean(),
    checked_at: IsoDateTime,
    run_id: z.string().max(32).nullable(),
    problems: z.array(z.string().max(1000)).max(20),
  })
  .strict()
  .meta({ id: "FleetDriftV1" });

/** What holds an installation: a rollout, or a create or destroy run (the customer's lease). */
export const FleetHoldV1 = z
  .object({ kind: z.enum(["rollout", "create", "destroy", "run"]), id: z.string().max(64) })
  .strict()
  .meta({ id: "FleetHoldV1" });

export const FleetInstallationV1 = z
  .object({
    customer: FleetCustomerV1,
    environment: FleetEnvironmentV1,
    internal: z.boolean(),
    account: z
      .string()
      .regex(/^\d{12}$/)
      .nullable(),
    region: z.string().max(32).nullable(),
    release: FleetCommitShaV1.nullable(),
    /** The version of `release` when it is a published release. */
    release_version: FleetVersionV1.nullable(),
    schema_version: z.string().max(1000).nullable(),
    state: FleetInstallationStateV1,
    held_by: FleetHoldV1.nullable(),
    drift: FleetDriftV1.nullable(),
    health: FleetHealthV1,
    updated_at: IsoDateTime.nullable(),
  })
  .strict()
  .meta({ id: "FleetInstallationV1" });
export type FleetInstallationV1 = z.infer<typeof FleetInstallationV1>;

/** The S-Trust staging installation of the platform account: read-only, never a rollout target. */
export const FleetStagingRowV1 = z
  .object({
    release: FleetCommitShaV1.nullable(),
    release_version: FleetVersionV1.nullable(),
    deployed_at: IsoDateTime.nullable(),
    run_url: z.url().nullable(),
  })
  .strict()
  .meta({ id: "FleetStagingRowV1" });

/** Query of `GET /platform/fleet/installations` (every filter optional). */
export const FleetListQueryV1 = z
  .object({
    customer: FleetCustomerV1.optional(),
    environment: FleetEnvironmentV1.optional(),
    state: FleetInstallationStateV1.optional(),
    release: z
      .string()
      .regex(/^[0-9a-f]{7,40}$|^v\d+\.\d+\.\d+$/)
      .optional(),
    health: FleetHealthStatusV1.optional(),
    include_destroyed: z.enum(["true", "false"]).optional(),
  })
  .strict()
  .meta({ id: "FleetListQueryV1" });
export type FleetListQueryV1 = z.infer<typeof FleetListQueryV1>;

export const FleetListResponseV1 = z
  .object({ installations: z.array(FleetInstallationV1), staging: FleetStagingRowV1 })
  .strict()
  .meta({ id: "FleetListResponseV1" });
export type FleetListResponseV1 = z.infer<typeof FleetListResponseV1>;

export const FleetInstallationParamsV1 = z
  .object({ customer: FleetCustomerV1, environment: FleetEnvironmentV1 })
  .strict();

/** One entry of the console audit trail (spec "Console audit trail"; design D9). */
export const FleetAuditEntryV1 = z
  .object({
    at: IsoDateTime,
    action: z.string().max(64),
    actor: z.string().max(256).nullable(),
    customer: z.string().max(64).nullable(),
    environment: z.string().max(32).nullable(),
    request_id: z.string().max(64).nullable(),
    rollout_id: z.string().max(64).nullable(),
    run_id: z.string().max(32).nullable(),
    outcome: z.enum(["ok", "refused"]),
    reason: z.string().max(1000).nullable(),
  })
  .strict()
  .meta({ id: "FleetAuditEntryV1" });
export type FleetAuditEntryV1 = z.infer<typeof FleetAuditEntryV1>;

/** The metrics of the last hour that the customer account shares with the platform account (OAM). */
export const FleetMetricsV1 = z
  .object({
    requests_per_minute: z.number().nonnegative().nullable(),
    latency_p95_ms: z.number().nonnegative().nullable(),
    error_5xx_rate: z.number().min(0).max(1).nullable(),
    read_at: IsoDateTime,
  })
  .strict()
  .meta({ id: "FleetMetricsV1" });

export const FleetInstallationDetailV1 = FleetInstallationV1.extend({
  metrics: FleetMetricsV1.nullable(),
  /** Why the metrics could not be read (the health is then unknown); `null` when they were read. */
  metrics_error: z.string().max(500).nullable(),
  alarms: z.array(
    z
      .object({ name: z.string().max(256), state: z.enum(["OK", "ALARM", "INSUFFICIENT_DATA"]) })
      .strict(),
  ),
  audit: z.array(FleetAuditEntryV1),
})
  .strict()
  .meta({ id: "FleetInstallationDetailV1" });
export type FleetInstallationDetailV1 = z.infer<typeof FleetInstallationDetailV1>;

/** A build of the catalog (spec "Release catalog"), newest first. */
export const FleetReleaseV1 = z
  .object({
    release: FleetCommitShaV1,
    title: z.string().max(500),
    committed_at: IsoDateTime.nullable(),
    pushed_at: IsoDateTime.nullable(),
    version: FleetVersionV1.nullable(),
    /** A build goes to staging installations only; a published release anywhere. */
    deployable_to: z.array(FleetEnvironmentV1),
  })
  .strict()
  .meta({ id: "FleetReleaseV1" });
export type FleetReleaseV1 = z.infer<typeof FleetReleaseV1>;

export const FleetReleaseListResponseV1 = z
  .object({ releases: z.array(FleetReleaseV1) })
  .strict()
  .meta({ id: "FleetReleaseListResponseV1" });

/**
 * The step-up of a change (design D2): the Platform Administrator's password and current TOTP code,
 * confirmed by `iam` against the installation's identity store in the same call; never stored,
 * logged or answered.
 */
export const FleetStepUpV1 = z
  .object({ password: z.string().min(1).max(256), code: z.string().regex(/^\d{6}$/) })
  .strict()
  .meta({ id: "FleetStepUpV1" });
export type FleetStepUpV1 = z.infer<typeof FleetStepUpV1>;

export const FleetTargetV1 = z
  .object({ customer: FleetCustomerV1, environment: FleetEnvironmentV1 })
  .strict()
  .meta({ id: "FleetTargetV1" });
export type FleetTargetV1 = z.infer<typeof FleetTargetV1>;

/** `POST /platform/fleet/rollouts/plan`: the plan of a release on chosen targets (spec "Rollout plan before launch"). */
export const FleetPlanRequestV1 = z
  .object({
    release: FleetCommitShaV1,
    targets: z.array(FleetTargetV1).min(1).max(256),
    canary: FleetTargetV1.nullable().optional(),
    batch_size: z.int().min(1).max(20).optional(),
  })
  .strict()
  .meta({ id: "FleetPlanRequestV1" });
export type FleetPlanRequestV1 = z.infer<typeof FleetPlanRequestV1>;

export const FleetPlanTargetV1 = z
  .object({
    customer: FleetCustomerV1,
    environment: FleetEnvironmentV1,
    from: FleetCommitShaV1.nullable(),
    to: FleetCommitShaV1,
    /** `true`: a failure leaves the installation failed-forward (no automatic rollback); `null`: not known. */
    database_change: z.boolean().nullable(),
    excluded: z.boolean(),
    reason: z.string().max(500).nullable(),
    /** Why it is excluded, as a stable code (the console translates it). */
    reason_code: z
      .enum(["not_in_registry", "not_at_rest", "held_by_run", "not_published", "same_release", "downgrade", "diverged"])
      .nullable(),
    /** The state of an installation that is not at rest. */
    state: z.string().max(32).nullable(),
  })
  .strict()
  .meta({ id: "FleetPlanTargetV1" });

export const FleetPlanV1 = z
  .object({
    release: FleetCommitShaV1,
    release_version: FleetVersionV1.nullable(),
    targets: z.array(FleetPlanTargetV1),
    canary: FleetTargetV1.nullable(),
    batch_size: z.int().min(1).max(20),
    /** Production installations the rollout would upgrade (typed again to confirm the launch). */
    production_count: z.int().min(0),
    /** Why nothing can be launched (a halted or running rollout, the canary excluded, no target left). */
    blocked: z.string().max(500).nullable(),
    blocked_code: z.enum(["not_a_build", "halted_rollout", "rollout_running", "canary_excluded", "no_target"]).nullable(),
    /** Digest of the plan: the launch is refused (`409 conflict`, reason `plan_changed`) when the registry no longer matches it. */
    digest: z.string().min(1).max(128),
  })
  .strict()
  .meta({ id: "FleetPlanV1" });
export type FleetPlanV1 = z.infer<typeof FleetPlanV1>;

/** `POST /platform/fleet/rollouts`: launches the plan whose digest it names. */
export const FleetRolloutRequestV1 = FleetPlanRequestV1.extend({
  plan_digest: z.string().min(1).max(128),
  /** The number of production installations typed on the confirmation screen; required when the plan has any. */
  confirm_production_count: z.int().min(0).optional(),
  step_up: FleetStepUpV1,
})
  .strict()
  .meta({ id: "FleetRolloutRequestV1" });
export type FleetRolloutRequestV1 = z.infer<typeof FleetRolloutRequestV1>;

export const FleetRequestOperationV1 = z
  .enum([
    "customer-create",
    "customer-destroy",
    "customer-configure",
    "rollout-start",
    "rollout-clear",
    "release-publish",
  ])
  .meta({ id: "FleetRequestOperationV1" });
export const FleetRequestStatusV1 = z
  .enum(["pending-approval", "ready", "dispatched", "rejected", "expired"])
  .meta({ id: "FleetRequestStatusV1" });
export type FleetRequestStatusV1 = z.infer<typeof FleetRequestStatusV1>;

/** A console request (design D3), without its free-form inputs beyond what the tab shows. */
export const FleetRequestV1 = z
  .object({
    request_id: z.string().max(64),
    operation: FleetRequestOperationV1,
    status: FleetRequestStatusV1,
    requester: z.string().max(256),
    approver: z.string().max(256).nullable(),
    /** When a second Platform Administrator approved it (a production destroy). */
    approved_at: IsoDateTime.nullable(),
    created_at: IsoDateTime,
    expires_at: IsoDateTime,
    run_id: z.string().max(32).nullable(),
    run_url: z.url().nullable(),
    customer: z.string().max(64).nullable(),
    include_production: z.boolean().nullable(),
    release: FleetCommitShaV1.nullable(),
    version: FleetVersionV1.nullable(),
    rollout_id: z.string().max(64).nullable(),
    target_count: z.int().min(0).nullable(),
    /** The production installations a rollout request names. */
    production_count: z.int().min(0).nullable(),
    /** The environments a customer create names (`production` makes it a production change). */
    environments: z.array(FleetEnvironmentV1).max(2).nullable(),
    /** The installations a rollout request names, its canary and batch size: what to plan again. */
    targets: z.array(FleetTargetV1).max(256).nullable(),
    canary: FleetTargetV1.nullable(),
    batch_size: z.int().min(1).max(20).nullable(),
  })
  .strict()
  .meta({ id: "FleetRequestV1" });
export type FleetRequestV1 = z.infer<typeof FleetRequestV1>;

/**
 * The answer of a change: the recorded request and whether its workflow was dispatched (a production
 * destroy waits for its approval; a dispatch that failed leaves the request ready, to retry).
 */
export const FleetRequestAcceptedV1 = z
  .object({ request: FleetRequestV1, dispatched: z.boolean() })
  .strict()
  .meta({ id: "FleetRequestAcceptedV1" });
export type FleetRequestAcceptedV1 = z.infer<typeof FleetRequestAcceptedV1>;

export const FleetRequestListQueryV1 = z
  .object({ status: FleetRequestStatusV1.optional() })
  .strict()
  .meta({ id: "FleetRequestListQueryV1" });
export const FleetRequestListResponseV1 = z
  .object({ requests: z.array(FleetRequestV1) })
  .strict()
  .meta({ id: "FleetRequestListResponseV1" });
/** `GET /platform/fleet/requests/{id}`: one console request. */
export const FleetRequestResponseV1 = z
  .object({ request: FleetRequestV1 })
  .strict()
  .meta({ id: "FleetRequestResponseV1" });
export type FleetRequestResponseV1 = z.infer<typeof FleetRequestResponseV1>;
export const FleetRequestParamsV1 = z
  .object({ id: z.string().regex(/^[A-Za-z0-9._-]{1,64}$/) })
  .strict();

export const FleetPublishRequestV1 = z
  .object({ release: FleetCommitShaV1, version: FleetVersionV1, step_up: FleetStepUpV1 })
  .strict()
  .meta({ id: "FleetPublishRequestV1" });
export type FleetPublishRequestV1 = z.infer<typeof FleetPublishRequestV1>;

export const FleetRolloutTargetStateV1 = z
  .enum([
    "waiting",
    "upgrading",
    "passed",
    "rolled_back",
    "failed_forward",
    "failed",
    "not_started",
  ])
  .meta({ id: "FleetRolloutTargetStateV1" });

export const FleetRolloutV1 = z
  .object({
    rollout_id: z.string().max(64),
    state: z.enum(["running", "halted", "completed", "aborted"]),
    release: FleetCommitShaV1,
    targets: z.array(
      z
        .object({
          customer: z.string().max(64),
          environment: FleetEnvironmentV1,
          state: FleetRolloutTargetStateV1,
        })
        .strict(),
    ),
    halt_requested: z
      .object({
        actor: z.string().max(256),
        reason: z.string().max(1000).nullable(),
        at: IsoDateTime,
      })
      .strict()
      .nullable(),
    error: z.string().max(1000).nullable(),
    started_at: IsoDateTime.nullable(),
    updated_at: IsoDateTime.nullable(),
    run_url: z.url().nullable(),
    audit: z.array(FleetAuditEntryV1),
  })
  .strict()
  .meta({ id: "FleetRolloutV1" });
export type FleetRolloutV1 = z.infer<typeof FleetRolloutV1>;

export const FleetRolloutResponseV1 = z
  .object({ rollout: FleetRolloutV1.nullable() })
  .strict()
  .meta({ id: "FleetRolloutResponseV1" });
export type FleetRolloutResponseV1 = z.infer<typeof FleetRolloutResponseV1>;
export const FleetRolloutParamsV1 = z
  .object({ id: z.string().regex(/^[A-Za-z0-9._-]{1,64}$/) })
  .strict();

/** `POST /platform/fleet/rollouts/{id}/halt`: no step-up (it only stops, and acts at once). */
export const FleetHaltRequestV1 = z
  .object({ reason: z.string().min(1).max(500) })
  .strict()
  .meta({ id: "FleetHaltRequestV1" });
export const FleetStepUpRequestV1 = z
  .object({ step_up: FleetStepUpV1 })
  .strict()
  .meta({ id: "FleetStepUpRequestV1" });
export const FleetRejectRequestV1 = z
  .object({ reason: z.string().min(1).max(500), step_up: FleetStepUpV1 })
  .strict()
  .meta({ id: "FleetRejectRequestV1" });

/** One operator of a customer creation: slug, display name and contracted products (design D14 item 2). */
export const FleetOperatorV1 = z
  .object({
    slug: z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/),
    name: z.string().min(1).max(80),
    products: z.array(z.enum(PRODUCTS)).min(1).max(PRODUCTS.length),
  })
  .strict()
  .meta({ id: "FleetOperatorV1" });
export type FleetOperatorV1 = z.infer<typeof FleetOperatorV1>;

/** `POST /platform/fleet/customers`: the inputs of `customer-create.yml` (spec "Creating a customer from the console"). */
export const FleetCreateCustomerRequestV1 = z
  .object({
    customer: FleetCustomerV1,
    environments: z.array(FleetEnvironmentV1).min(1).max(2),
    internal: z.boolean().optional(),
    release: FleetCommitShaV1,
    account: z
      .string()
      .regex(/^\d{12}$/)
      .nullable()
      .optional(),
    alarm_emails: z.array(z.string().max(320)).max(20).optional(),
    production_eks_cidrs: z.array(z.string().max(64)).max(20).optional(),
    skip_service_linked_roles: z.array(z.string().max(64)).max(20).optional(),
    /**
     * The customer's first Administrator's e-mail: the provisioning Job of each installation creates
     * them, and Cognito e-mails the temporary password (adopt-installation-per-customer design D14).
     */
    admin_email: z.string().min(3).max(254),
    /** The customer's security contacts, on the topic `<stack>-console-security` (required for a real customer). */
    security_emails: z.array(z.string().max(320)).max(20).optional(),
    /** The customer's Organization in its installations (required for a real customer). */
    organization_name: z.string().min(1).max(80).optional(),
    /** The customer's operators with their contracted products (required for a real customer). */
    operators: z.array(FleetOperatorV1).min(1).max(20).optional(),
    step_up: FleetStepUpV1,
  })
  .strict()
  .meta({ id: "FleetCreateCustomerRequestV1" });
export type FleetCreateCustomerRequestV1 = z.infer<typeof FleetCreateCustomerRequestV1>;

/** `POST /platform/fleet/customers/{customer}/destroy`: typed confirmation (`<customer>` or `<customer> production`). */
export const FleetDestroyCustomerRequestV1 = z
  .object({ include_production: z.boolean(), confirm: z.string().max(64), step_up: FleetStepUpV1 })
  .strict()
  .meta({ id: "FleetDestroyCustomerRequestV1" });
export type FleetDestroyCustomerRequestV1 = z.infer<typeof FleetDestroyCustomerRequestV1>;
/**
 * `POST /platform/fleet/customers/{customer}/configure`: the customer's operators in the named
 * installations (default: every installation), run by the provisioning Job as the system
 * (adopt-installation-per-customer design D14 item 4, task 6.14): new operators with their
 * products, more products on existing ones, deactivations with their reason. At least one change.
 */
export const FleetConfigureCustomerRequestV1 = z
  .object({
    environments: z.array(FleetEnvironmentV1).min(1).max(2).optional(),
    add: z.array(FleetOperatorV1).max(20).optional(),
    enable_products: z
      .array(
        z
          .object({
            slug: z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/),
            products: z.array(z.enum(PRODUCTS)).min(1).max(PRODUCTS.length),
          })
          .strict(),
      )
      .max(20)
      .optional(),
    deactivate: z
      .array(
        z
          .object({
            slug: z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/),
            reason: z.string().trim().min(1).max(500),
          })
          .strict(),
      )
      .max(20)
      .optional(),
    step_up: FleetStepUpV1,
  })
  .strict()
  .meta({ id: "FleetConfigureCustomerRequestV1" });
export type FleetConfigureCustomerRequestV1 = z.infer<typeof FleetConfigureCustomerRequestV1>;
export const FleetCustomerParamsV1 = z.object({ customer: FleetCustomerV1 }).strict();

export const FleetAuditQueryV1 = z
  .object({
    actor: z.string().min(1).max(256).optional(),
    customer: FleetCustomerV1.optional(),
    environment: FleetEnvironmentV1.optional(),
    rollout: z
      .string()
      .regex(/^[A-Za-z0-9._-]{1,64}$/)
      .optional(),
    action: z
      .string()
      .regex(/^[a-z][a-z0-9-]{0,63}$/)
      .optional(),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
    /** Opaque cursor of the next page (`next_cursor` of the previous one). */
    cursor: z
      .string()
      .regex(/^\d{1,6}$/)
      .optional(),
  })
  .strict()
  .meta({ id: "FleetAuditQueryV1" });
export type FleetAuditQueryV1 = z.infer<typeof FleetAuditQueryV1>;

export const FleetAuditResponseV1 = z
  .object({ entries: z.array(FleetAuditEntryV1).max(100), next_cursor: z.string().nullable() })
  .strict()
  .meta({ id: "FleetAuditResponseV1" });
export type FleetAuditResponseV1 = z.infer<typeof FleetAuditResponseV1>;

/**
 * Reasons of `409 conflict` on the Fleet API (`details.reason`): the console maps each one to a
 * message. A refusal of the registry's own rules that has no stable code answers `registry_refused`.
 */
export const FLEET_CONFLICT_REASONS = [
  "plan_changed",
  "plan_blocked",
  "production_count_mismatch",
  "rollout_running",
  "halted_rollout",
  "rollout_not_running",
  "not_published",
  "not_a_build",
  "version_exists",
  "already_published",
  "version_not_greater",
  "customer_exists",
  "account_taken",
  "customer_not_found",
  "confirmation_mismatch",
  "production_required",
  "no_production",
  "request_not_pending",
  "request_used",
  "run_refused",
  "not_a_console_request",
  "request_expired",
  "self_approval",
  "two_factor_not_enabled",
  "registry_refused",
] as const;
export type FleetConflictReason = (typeof FLEET_CONFLICT_REASONS)[number];
